/**
 * User Form Component
 *
 * Reusable form for creating and editing users.
 * Uses react-hook-form with Zod validation.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@lognai/synapse-design-system-v2';
import { Input } from '@lognai/synapse-design-system-v2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { Badge, Button, Label } from '@lognai/synapse-design-system-v2';
import { X } from '@lognai/synapse-design-system-v2/icons';
import { getCompanyId } from '@lognai/plugin-sdk';
import { userFormSchema, type UserFormData } from './UserFormSchema';
import { CompanyCombobox, CompanyName } from '../shared/CompanyCombobox';
import { CompanyRoleSelect } from '../roles/CompanyRoleSelect';
import {
  useUser,
  useAddSecondaryCompany,
  useRemoveSecondaryCompany,
} from '../../hooks';
import { UserStatus } from '../../types/domain-enums';
import type { User } from '@lognai/core-platform';

export interface UserFormProps {
  user?: User;
  onSubmit: (data: UserFormData) => void | Promise<void>;
  isSubmitting?: boolean;
}

// `isSubmitting` stays on the props contract for the modals that pass it; the
// submit control lives in the modal, so the form itself has nothing to disable.
export function UserForm({ user, onSubmit }: UserFormProps) {
  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: user?.email || '',
      fullName: user?.fullName || '',
      // A company admin can only create users in their own company — start
      // there, so the role picker below is usable without a redundant step.
      companyId: user?.companyId || getCompanyId() || '',
      username: user?.username || '',
      department: user?.department || '',
      jobTitle: user?.jobTitle || '',
      employeeId: user?.employeeId || '',
      phoneNumber: user?.phoneNumber || '',
      roleId: '',
      // A new user is Pending until they set their first password from the
      // set-password link (redeeming it activates them).
      status: user?.status || UserStatus.PENDING,
      timezone: user?.timezone || 'UTC',
      language: user?.language || 'en',
    },
  });

  // Update form when user prop changes
  useEffect(() => {
    if (user) {
      form.reset({
        email: user.email,
        fullName: user.fullName || '',
        companyId: user.companyId ?? '',
        username: user.username || '',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
        employeeId: user.employeeId || '',
        phoneNumber: user.phoneNumber || '',
        status: user.status,
        timezone: user.timezone || 'UTC',
        language: user.language || 'en',
      });
    }
  }, [user, form]);

  const isEditMode = !!user;
  const companyIdValue = form.watch('companyId');

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isEditMode) {
      // Roles of an existing user are managed on its Roles page.
      const { roleId: _roleId, ...profile } = data;
      await onSubmit(profile);
      return;
    }
    await onSubmit(data);
  });

  // Read from the server rather than the row this modal was opened with, so a
  // grant made here is reflected without closing and reopening.
  const { data: liveUser } = useUser(user?.id, { enabled: Boolean(user?.id) });
  const secondaryCompanyIds = (liveUser ?? user)?.secondaryCompanyIds ?? [];
  const addSecondary = useAddSecondaryCompany();
  const removeSecondary = useRemoveSecondaryCompany();
  const [companyToGrant, setCompanyToGrant] = React.useState('');

  /**
   * Who may be granted, which is narrower than "any company".
   *
   * `addSecondaryCompanyHandler` lets a platform admin grant membership of any
   * company, but a TENANT admin only of their own — an Acme administrator must
   * not be able to hand an Acme user the keys to another tenant. So a tenant
   * admin's only legitimate use of this control is adding THIS company to a
   * user whose primary company is elsewhere (a partner user, say), and offering
   * them the full list would just produce a 403 (QA ledger AUTH-012).
   */
  const ownCompanyId = getCompanyId();
  const isPlatformAdmin = ownCompanyId == null;
  const isGrantable = (company: { id: string }): boolean =>
    company.id !== user?.companyId &&
    !secondaryCompanyIds.includes(company.id) &&
    (isPlatformAdmin || company.id === ownCompanyId);
  // A platform admin's candidates live on the server and are searched; a
  // tenant admin has at most one (their own company), known up front.
  const canGrantAny =
    isPlatformAdmin || (ownCompanyId != null && isGrantable({ id: ownCompanyId }));


  return (
    <Form {...form}>
      {/* `noValidate`: the browser's own constraint UI is off so one field does
          not answer in a different voice from every other field in this form.
          The zod schema validates the same address in the app's styling, and
          `type="email"` stays for the keyboard and autofill (QA ledger
          FORM-001). */}
      <form noValidate onSubmit={handleSubmit} className="space-y-6">
        {/* Email */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  disabled={isEditMode}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              {isEditMode && (
                <FormDescription>Email cannot be changed after creation</FormDescription>
              )}
            </FormItem>
          )}
        />

        {/* Full Name */}
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Company */}
        <FormField
          control={form.control}
          name="companyId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company *</FormLabel>
              <FormControl>
                <CompanyCombobox
                  value={field.value}
                  onChange={(companyId) => field.onChange(companyId)}
                  placeholder="Select company"
                  disabled={isEditMode}
                />
              </FormControl>
              <FormMessage />
              {isEditMode && (
                <FormDescription>
                  A user&apos;s primary company cannot be changed. Grant access to further
                  companies below.
                </FormDescription>
              )}
            </FormItem>
          )}
        />

        {/* Role — create only. An existing user's roles are managed on their
            Roles page, where a user can hold several. */}
        {!isEditMode && (
          <FormField
            control={form.control}
            name="roleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <CompanyRoleSelect
                  companyId={companyIdValue}
                  value={field.value}
                  onChange={field.onChange}
                />
                <FormMessage />
                <FormDescription>
                  What the user can do. More roles can be added later from their Roles page.
                </FormDescription>
              </FormItem>
            )}
          />
        )}

        {/* Secondary companies — the control the copy above used to promise and
            never had, which is also what made a multi-company session
            impossible to set up (QA ledger AUTH-012, AUTH-014). */}
        {isEditMode && user && (
          // Plain markup, not the Form primitives: this is not a field of the
          // form being submitted — each grant is applied immediately through
          // its own endpoint — and FormLabel/FormDescription require a
          // FormField context they would not have here.
          <div className="space-y-2">
            <Label>Other companies this user can access</Label>
            <div className="flex flex-wrap items-center gap-2">
              {secondaryCompanyIds.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  None — this user can only see{' '}
                  {user.companyId ? (
                    <CompanyName id={user.companyId} fallback="their own company" />
                  ) : (
                    'their own company'
                  )}
                  .
                </span>
              )}
              {secondaryCompanyIds.map((id) => (
                <Badge key={id} variant="outline" className="gap-1 pr-1">
                  <CompanyName id={id} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    aria-label={`Remove access to company ${id}`}
                    disabled={removeSecondary.isPending}
                    onClick={() => removeSecondary.mutate({ userId: user.id, companyId: id })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
            {canGrantAny ? (
              <div className="flex items-center gap-2 pt-1">
                <div className="w-full max-w-xs">
                  <CompanyCombobox
                    value={companyToGrant}
                    onChange={setCompanyToGrant}
                    isSelectable={isGrantable}
                    placeholder="Choose a company…"
                    emptyText="No grantable companies match."
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!companyToGrant || addSecondary.isPending}
                  onClick={() => {
                    addSecondary.mutate(
                      { userId: user.id, companyId: companyToGrant },
                      { onSuccess: () => setCompanyToGrant('') }
                    );
                  }}
                >
                  {addSecondary.isPending ? 'Granting…' : 'Grant access'}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {isPlatformAdmin
                  ? 'There are no other companies to grant access to.'
                  : 'This user already has access to your company. Only a platform administrator can grant access to another one.'}
              </p>
            )}
            {(addSecondary.isError || removeSecondary.isError) && (
              <p role="alert" className="text-sm text-destructive">
                {((addSecondary.error ?? removeSecondary.error) as Error)?.message ||
                  'Could not change company access'}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {/* Username */}
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="johndoe" {...field} />
                </FormControl>
                <FormMessage />
                <FormDescription>Optional, min 3 characters if provided</FormDescription>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Department */}
          <FormField
            control={form.control}
            name="department"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Department</FormLabel>
                <FormControl>
                  <Input placeholder="Sales" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Job Title */}
          <FormField
            control={form.control}
            name="jobTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Title</FormLabel>
                <FormControl>
                  <Input placeholder="Sales Manager" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Employee ID */}
          <FormField
            control={form.control}
            name="employeeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee ID</FormLabel>
                <FormControl>
                  <Input placeholder="EMP-12345" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Number */}
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="+1 (555) 123-4567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Status */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    {/* The server refuses to create a suspended/inactive user. */}
                    {isEditMode && <SelectItem value="inactive">Inactive</SelectItem>}
                    {isEditMode && <SelectItem value="suspended">Suspended</SelectItem>}
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Timezone */}
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Timezone</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Kolkata">India</SelectItem>
                    <SelectItem value="Asia/Singapore">Singapore</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Language */}
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Language</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="zh">Chinese</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}